'use client';

import { ConversationWInterview } from '@/types/pulsedb';
import { Tables } from '@/types/db';
import { formatDateTime } from '@/utils/helpers';
import { InterviewModality } from '@/types/pulse';
import {
  conversationDuration,
  conversationStartedAt
} from '@/utils/pulse/interview';
import { FileTextIcon } from 'lucide-react';
import Link from 'next/link';
import ConversationVideoPlayer from '../../common/recordings/ConversationVideoPlayer';
import { ConversationModalityThumbnail } from './ConversationModalityThumbnail';
import { TextLink } from '@/components/ui/TextLink';

type ConversationWithOptionalInterview = Tables<'conversations'> & {
  interviews?: { id: number; slug: string; name: string | null };
};

export function ConversationPreview({
  conversation,
  interviewSlug,
  showInterviewName = true
}: {
  conversation: ConversationWithOptionalInterview;
  interviewSlug?: string;
  showInterviewName?: boolean;
}) {
  const resolvedInterviewSlug = conversation.interviews?.slug ?? interviewSlug;
  const interviewName = conversation.interviews?.name;

  const isTextModality = conversation.modality === InterviewModality.TEXT;

  return (
    <div>
      <div className="overflow-hidden rounded-lg">
        {isTextModality && !conversation.processing ? (
          <ConversationModalityThumbnail
            conversation={conversation}
            interviewSlug={resolvedInterviewSlug}
          />
        ) : (
          <ConversationVideoPlayer
            conversation={conversation as ConversationWInterview}
            inPreviewDisplay={true}
          />
        )}
      </div>
      <div className="flex items-center justify-between px-1 pt-1">
        {showInterviewName && interviewName ? (
          <TextLink
            variant="quiet"
            href={`/account/interview/${resolvedInterviewSlug}/conversation?cid=${conversation.interview_conversation_id}`}
            className="text-sm"
          >
            {interviewName}
          </TextLink>
        ) : (
          <TextLink
            variant="quiet"
            href={`/account/interview/${resolvedInterviewSlug}/conversation?cid=${conversation.interview_conversation_id}`}
            className="text-xs text-muted-foreground"
          >
            ID {conversation.interview_conversation_id}
          </TextLink>
        )}
        <span className="text-right text-xs text-muted-foreground">
          {formatDateTime(conversationStartedAt(conversation))}
        </span>
      </div>
    </div>
  );
}

// May or may not merge with ConversationPreview depending on how utilized it is
function ConversationPreviewTextSlim({
  conversation,
  interviewSlug,
  showInterviewName
}: {
  conversation: ConversationWithOptionalInterview;
  interviewSlug?: string;
  showInterviewName: boolean;
}) {
  const resolvedInterviewSlug = conversation.interviews?.slug ?? interviewSlug;
  const interviewName = conversation.interviews?.name;
  const duration = conversationDuration(conversation);
  const href = `/account/interview/${resolvedInterviewSlug}/conversation?cid=${conversation.interview_conversation_id}`;
  return (
    <Link
      href={href}
      className="flex items-center gap-3 rounded-lg border border-border bg-muted px-4 py-3 transition-colors hover:bg-muted"
    >
      <FileTextIcon className="size-4 shrink-0 text-zinc-400" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-zinc-700">
          {showInterviewName && interviewName
            ? interviewName
            : `Conversation ${conversation.interview_conversation_id}`}
        </p>
        <p className="text-xs text-zinc-400">
          {formatDateTime(conversationStartedAt(conversation))}
        </p>
      </div>
      {duration && (
        <span className="shrink-0 text-xs tabular-nums text-zinc-400">
          {duration}
        </span>
      )}
    </Link>
  );
}

export default function ConversationsGrid({
  conversations,
  interviewSlug,
  showInterviewName = true
}: {
  conversations: ConversationWithOptionalInterview[];
  interviewSlug?: string;
  showInterviewName?: boolean;
}) {
  const allText = conversations.every(
    (c) => c.modality === InterviewModality.TEXT
  );
  if (allText) {
    return (
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {conversations.map((conversation) => (
          <ConversationPreviewTextSlim
            key={conversation.id}
            conversation={conversation}
            interviewSlug={interviewSlug}
            showInterviewName={showInterviewName ?? true}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      {conversations.map((conversation) => (
        <div key={conversation.id}>
          <ConversationPreview
            conversation={conversation}
            interviewSlug={interviewSlug}
            showInterviewName={showInterviewName}
          />
        </div>
      ))}
    </div>
  );
}
